import { create } from 'zustand';
import { api } from '@/lib/api';
import { engagementApi } from '@/lib/engagement-api';
import type { Notification, ApiResponse } from '@/types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  systemUnreadCount: number;
  inboxUnreadCount: number;
  approvalUnreadCount: number;
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  reset: () => void;
}

let sessionRevision = 0;
let systemRevision = 0;

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  systemUnreadCount: 0,
  inboxUnreadCount: 0,
  approvalUnreadCount: 0,

  fetchNotifications: async () => {
    const revision = sessionRevision;
    const systemVersion = systemRevision;
    const res = await api.get<ApiResponse<Notification[]>>('/notifications');
    if (revision === sessionRevision && systemVersion === systemRevision && res.success && res.data) set({ notifications: res.data });
  },

  fetchUnreadCount: async () => {
    const revision = sessionRevision;
    const systemVersion = systemRevision;
    const [res, inbox, approvals] = await Promise.allSettled([
      api.get<ApiResponse<{ count: number }>>('/notifications/unread-count'),
      engagementApi.inboxUnread(),
      engagementApi.approvalCount(),
    ]);
    if (revision !== sessionRevision) return;
    set((state) => {
      const systemUnreadCount = systemVersion === systemRevision && res.status === 'fulfilled' && res.value.success && res.value.data
        ? res.value.data.count : state.systemUnreadCount;
      const inboxUnreadCount = inbox.status === 'fulfilled' ? inbox.value.count : state.inboxUnreadCount;
      const approvalUnreadCount = approvals.status === 'fulfilled' ? approvals.value.count : state.approvalUnreadCount;
      return { systemUnreadCount, inboxUnreadCount, approvalUnreadCount,
        unreadCount: systemUnreadCount + inboxUnreadCount + approvalUnreadCount };
    });
  },

  markAsRead: async (id) => {
    const revision = sessionRevision;
    const res = await api.patch<ApiResponse<Notification>>(`/notifications/${id}/read`);
    if (!res.success) throw new Error(res.error || 'Benachrichtigung konnte nicht als gelesen markiert werden.');
    if (revision !== sessionRevision) return;
    systemRevision += 1;
    set((s) => {
      const wasUnread = s.notifications.some((n) => n.id === id && !n.is_read);
      const systemUnreadCount = Math.max(0, s.systemUnreadCount - (wasUnread ? 1 : 0));
      return {
        notifications: s.notifications.map(n => n.id === id ? { ...n, is_read: true } : n),
        systemUnreadCount,
        unreadCount: systemUnreadCount + s.inboxUnreadCount + s.approvalUnreadCount,
      };
    });
  },

  markAllAsRead: async () => {
    const revision = sessionRevision;
    const res = await api.post<ApiResponse<unknown>>('/notifications/mark-all-read');
    if (!res.success) throw new Error(res.error || 'Benachrichtigungen konnten nicht als gelesen markiert werden.');
    if (revision !== sessionRevision) return;
    systemRevision += 1;
    set((s) => ({
      notifications: s.notifications.map(n => ({ ...n, is_read: true })),
      systemUnreadCount: 0,
      unreadCount: s.inboxUnreadCount + s.approvalUnreadCount,
    }));
  },

  reset: () => {
    sessionRevision += 1;
    systemRevision += 1;
    set({ notifications: [], unreadCount: 0, systemUnreadCount: 0, inboxUnreadCount: 0, approvalUnreadCount: 0 });
  },
}));
